import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  ChartBar, 
  TrendUp, 
  Trophy, 
  Target,
  CaretRight,
  Info
} from "phosphor-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

// Profit Factor color based on value
const getProfitFactorColor = (pf) => {
  if (pf === null || pf === undefined) return "text-white/50";
  const pfValue = parseFloat(pf) || 0;
  if (pfValue < 1.0) return "text-[#FF3B30]";
  if (pfValue <= 1.5) return "text-[#FFC107]";
  return "text-[#00E676]";
};

// Profit Factor friendly label
const getProfitFactorLabel = (pf) => {
  if (pf === null || pf === undefined) return "N/A";
  const pfValue = parseFloat(pf);
  if (isNaN(pfValue)) return "N/A";
  if (pfValue < 1.0) return "Unprofitable";
  if (pfValue <= 1.5) return "Decent";
  return "Strong";
};

const MetricCard = ({ title, value, subtitle, icon: Icon, highlight = false }) => (
  <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
    <div className="flex items-start justify-between mb-3">
      <div className="text-white/40 text-xs font-mono uppercase tracking-wider">{title}</div>
      {Icon && <Icon size={20} weight="duotone" className="text-white/40" />}
    </div>
    <div className={`text-3xl font-bold font-mono mb-1 ${highlight ? highlight : 'text-white'}`}>
      {value}
    </div>
    {subtitle && <div className="text-white/60 text-sm">{subtitle}</div>}
  </div>
);

// Chart colors
const CHART_COLORS = ['#00E676', '#00B8D4', '#FFC107', '#FF6B6B', '#9C27B0', '#2196F3', '#FF9800'];

export default function AssetGroupDetail() {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [comparisonMetric, setComparisonMetric] = useState('win_rate');

  useEffect(() => {
    fetchGroupData();
  }, [groupId]);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/asset-groups/${groupId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching asset group analytics:", error);
      if (error.response?.status === 404) {
        toast.error("Asset group not found");
      } else {
        toast.error("Failed to load asset group analytics");
      }
      navigate('/analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupClick = (setupId) => {
    navigate(`/analytics/setup/${setupId}`);
  };

  // Get group description based on type
  const getGroupDescription = () => {
    if (!analytics) return "";
    
    const { group_type, assets, excluded_group_names } = analytics;
    
    if (group_type === 'exclude_group' && excluded_group_names && excluded_group_names.length > 0) {
      if (excluded_group_names.length === 1) {
        return `Viewing assets excluding ${excluded_group_names[0]}`;
      } else if (excluded_group_names.length <= 3) {
        return `Viewing assets excluding ${excluded_group_names.join(', ')}`;
      }
      return `Viewing assets excluding ${excluded_group_names.length} groups`;
    } else if (group_type === 'remaining_all') {
      return "Viewing all assets not in any manual group";
    } else if (assets && assets.length > 0) {
      if (assets.length <= 5) {
        return `Viewing performance for ${assets.join(', ')}`;
      }
      return `Viewing performance for ${assets.slice(0, 5).join(', ')} and ${assets.length - 5} more`;
    }
    return "No assets in this group yet";
  };

  // Prepare chart data - use win_rate_excluding_even for comparative charts
  const chartData = analytics?.setup_breakdown?.map((setup, index) => ({
    name: setup.setup_name,
    setup_id: setup.setup_id,
    win_rate: setup.win_rate_excluding_even ?? setup.win_rate,
    avg_rr: setup.avg_rr,
    profit_factor: setup.profit_factor === null ? 0 : setup.profit_factor,
    total_trades: setup.total_trades,
    color: CHART_COLORS[index % CHART_COLORS.length]
  })) || [];

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#00E676] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8">
        <div className="text-white/60 text-center">Asset group not found</div>
      </div>
    );
  }

  const combined = analytics.combined;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="asset-group-detail-page">
      <div className="w-full max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center gap-2 text-white/50 hover:text-white mb-4 transition-colors"
            data-testid="back-to-analytics"
          >
            <ArrowLeft size={20} />
            <span>Back to Analytics</span>
          </button>
          
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight" data-testid="group-title">
                {analytics.group_name}
              </h2>
              {analytics.group_type && analytics.group_type !== 'manual' && (
                <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                  analytics.group_type === 'remaining_all' 
                    ? 'bg-[#00E676]/10 text-[#00E676]' 
                    : 'bg-[#FFC107]/10 text-[#FFC107]'
                }`}>
                  {analytics.group_type === 'remaining_all' ? 'AUTO' : 'EXCLUDE'}
                </span>
              )}
            </div>
            <p className="text-white/60 text-base font-sans">
              {getGroupDescription()}
            </p>
            <p className="text-white/40 text-sm mt-1">
              {analytics.assets?.length || 0} assets in this group
            </p>
          </div>
        </div>

        {/* Context Notice */}
        <div className="mb-6 bg-[#00B8D4]/5 border border-[#00B8D4]/20 rounded-sm p-4">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-[#00B8D4] shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-white/80 font-medium mb-1">Filtered Analytics</p>
              <p className="text-white/50">
                This view shows performance only for trades in this asset group. 
                Use this to understand which setups perform best in this market category.
              </p>
            </div>
          </div>
        </div>

        {/* Section A: Group Overview */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={24} weight="duotone" className="text-[#00E676]" />
            <h3 className="text-2xl font-bold font-heading">Group Performance</h3>
          </div>
          
          {combined.total_trades === 0 ? (
            <div className="bg-[#121212] border border-white/10 rounded-sm p-8 text-center">
              <ChartBar size={48} weight="duotone" className="text-white/40 mx-auto mb-4" />
              <p className="text-white/60">No trades found for this asset group yet</p>
              <p className="text-white/40 text-sm mt-2">
                Trade assets in this group to see performance analytics
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <MetricCard
                title="Total Trades"
                value={combined.total_trades}
                subtitle={`${combined.wins}W / ${combined.losses}L`}
                icon={ChartBar}
              />
              <MetricCard
                title="Win Rate"
                value={`${combined.win_rate_excluding_even ?? combined.win_rate}%`}
                subtitle="Excludes Even trades"
                icon={Target}
                highlight={(combined.win_rate_excluding_even ?? combined.win_rate) >= 50 ? "text-[#00E676]" : "text-[#FFC107]"}
              />
              <MetricCard
                title="Avg Win RR"
                value={combined.avg_win_rr !== null ? combined.avg_win_rr : "N/A"}
                subtitle="Winning trades only"
                icon={TrendUp}
                highlight="text-[#00E676]"
              />
              <MetricCard
                title="Average RR"
                value={combined.avg_rr}
                subtitle="All trades"
                icon={TrendUp}
                highlight={combined.avg_rr >= 2 ? "text-[#00E676]" : "text-white"}
              />
              <MetricCard
                title="Profit Factor"
                value={combined.profit_factor_infinite ? "∞" : (combined.profit_factor !== null ? combined.profit_factor : "N/A")}
                subtitle={combined.profit_factor_infinite ? "No losses" : getProfitFactorLabel(combined.profit_factor)}
                icon={Trophy}
                highlight={combined.profit_factor_infinite ? "text-[#00E676]" : getProfitFactorColor(combined.profit_factor)}
              />
            </div>
          )}
        </div>

        {/* Section B: Setup Performance within Asset Group */}
        {analytics.setup_breakdown && analytics.setup_breakdown.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Target size={24} weight="duotone" className="text-[#00B8D4]" />
              <h3 className="text-2xl font-bold font-heading">Setup Performance</h3>
              <span className="text-white/40 text-sm ml-2">within this asset group</span>
            </div>
            
            {/* Setup Comparison Table */}
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden mb-8">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#0A0A0A]/50 border-b border-white/10">
                      <th className="px-4 py-3 text-left text-white/40 text-xs font-mono uppercase">Setup</th>
                      <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Trades</th>
                      <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Win Rate</th>
                      <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Avg RR</th>
                      <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Avg Win RR</th>
                      <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase">Profit Factor</th>
                      <th className="px-4 py-3 text-right text-white/40 text-xs font-mono uppercase"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.setup_breakdown.map((setup, idx) => {
                      const pf = setup.profit_factor;
                      const pfInfinite = setup.profit_factor_infinite;
                      const pfDisplay = pfInfinite ? "∞" : (pf !== null ? pf : "N/A");
                      
                      return (
                        <tr 
                          key={setup.setup_id} 
                          className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                          onClick={() => handleSetupClick(setup.setup_id)}
                          data-testid={`setup-row-${setup.setup_id}`}
                        >
                          <td className="px-4 py-3 text-white font-medium">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                              />
                              {setup.setup_name}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-white/70 font-mono">
                            {setup.total_trades}
                          </td>
                          <td className={`px-4 py-3 text-right font-mono ${
                            (setup.win_rate_excluding_even ?? setup.win_rate) >= 50 ? 'text-[#00E676]' : 'text-[#FFC107]'
                          }`}>
                            {setup.win_rate_excluding_even ?? setup.win_rate}%
                          </td>
                          <td className={`px-4 py-3 text-right font-mono ${
                            setup.avg_rr >= 2 ? 'text-[#00E676]' : 'text-white/70'
                          }`}>
                            {setup.avg_rr}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-[#00E676]">
                            {setup.avg_win_rr !== null ? setup.avg_win_rr : 'N/A'}
                          </td>
                          <td className={`px-4 py-3 text-right font-mono ${pfInfinite ? 'text-[#00E676]' : getProfitFactorColor(pf)}`}>
                            {pfDisplay}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CaretRight size={16} className="text-white/30" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Visual Comparison Chart */}
            {chartData.length > 0 && (
              <div className="bg-[#121212] border border-white/10 rounded-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-white/60 text-sm font-medium">Visual Comparison</h4>
                  <select
                    value={comparisonMetric}
                    onChange={(e) => setComparisonMetric(e.target.value)}
                    className="bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-1.5 rounded-sm focus:outline-none"
                    data-testid="comparison-metric-select"
                  >
                    <option value="win_rate">Win Rate (%)</option>
                    <option value="avg_rr">Average RR</option>
                    <option value="profit_factor">Profit Factor</option>
                    <option value="total_trades">Total Trades</option>
                  </select>
                </div>
                
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={chartData}
                      margin={{ top: 10, right: 10, left: 10, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fill: '#888', fontSize: 11 }}
                        angle={-20}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        tick={{ fill: '#888', fontSize: 11 }}
                        domain={comparisonMetric === 'win_rate' ? [0, 100] : ['auto', 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #333',
                          borderRadius: '4px'
                        }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value) => [
                          comparisonMetric === 'win_rate' ? `${value}%` : 
                          comparisonMetric === 'avg_rr' ? `${value}R` : value,
                          comparisonMetric.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                        ]}
                      />
                      <Bar dataKey={comparisonMetric} radius={[4, 4, 0, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={entry.setup_id} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assets in Group */}
        {analytics.assets && analytics.assets.length > 0 && (
          <div className="mb-8">
            <div className="text-white/40 text-xs font-mono mb-3">ASSETS IN THIS GROUP</div>
            <div className="flex flex-wrap gap-2">
              {analytics.assets.map((asset, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-[#121212] border border-white/10 rounded-sm text-sm font-mono text-white/80"
                >
                  {asset}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
